import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { useEffect } from 'react';
import { AwardFlightsQueryParams, Coupon, TripType } from 'shared';

import { fetchFlights } from '../domain/api';
import { BookingStatus, useBookingStore } from '../domain/store';
import { generateKey } from '../domain/utils';

import { useBookingState } from './appHooks';

export const useFlightsPrefix = 'sky-flights';

export function useFlights(): UseQueryResult<Awaited<ReturnType<typeof fetchFlights>> | undefined, Error> {
  const { setCoupons } = useBookingStore();
  const bookingState = useBookingState();

  const {
    status,
    origin,
    destination,
    outboundDate,
    inboundDate,
    tripType,
    passengerCounts,
    selectedCoupons,
    isCepValid,
  } = bookingState;

  let payload: AwardFlightsQueryParams | undefined = undefined;

  if (origin && destination && outboundDate) {
    if (tripType === TripType.ONE_WAY) {
      payload = {
        origin,
        destination,
        outboundDate,
        tripType,
        selectedCouponCodes: selectedCoupons.map(coupon => coupon.code).toString(),
        ...passengerCounts,
      };
    } else if (tripType === TripType.ROUND_TRIP && inboundDate) {
      payload = {
        origin,
        destination,
        outboundDate,
        inboundDate,
        tripType,
        selectedCouponCodes: selectedCoupons.map(coupon => coupon.code).toString(),
        ...passengerCounts,
      };
    }
  }

  const enabled = isCepValid && status === BookingStatus.INITIATED;

  const key = payload ? generateKey(payload) : 'not-ready-to-fetch-flights';

  const query = useQuery({
    queryKey: [useFlightsPrefix, key],
    queryFn: () => {
      return payload ? fetchFlights(payload) : Promise.resolve(undefined);
    },
    gcTime: 1000 * 60 * 5, // 5 minutes
    staleTime: 1000 * 60 * 5,
    enabled,
    retry: false,
  });

  const flightsData = query.data;

  useEffect(() => {
    if (flightsData) {
      const coupons = bookingState.coupons || [];
      const newCoupons = flightsData.coupons || [];

      if (getCouponsKey(coupons) !== getCouponsKey(newCoupons)) {
        setCoupons(newCoupons);
      }
    }
  }, [flightsData]);

  if (query.isError) {
    throw query.error;
  }

  return query;
}

const getCouponsKey = (coupons: Coupon[]) => {
  return coupons
    .map(c => c.code)
    .sort()
    .join(',');
};
